import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { ApiService } from './api.service';
import { BaseService } from './base.service';
import { environment } from '../environments/environment';
import { IndexedDBService } from './indexed.db.service';
import { map, catchError, switchMap } from 'rxjs/operators';
import { HttpHeaders } from '@angular/common/http';
import moment from 'moment';

// Certificate interface
export interface Certificate {
  certificateId: string;
  certificateName: string;
}

// File interface
export interface RfqFile {
  name?: string;
  type?: string;
  size?: number;
  lastModified: number;
  fileName?: string;
  fileUrl?: string;
  fileType?: string;
}

// Interfaces for type safety
export interface RfqPart {
  rfqPartId: number;
  partName: string;
  partNumber: string;
  quantity: number;
  material: number;
  materialGrade: string;
  finishes: string[];
  tolerance: string;
  customTolerance?: string;
  notes: string;
  files: RfqFile[];
}

export interface RfqSummaryData {
  rfqId: string;
  rfqDetails: {
    rfqName: string;
    quoteBy: string;
    deliverBy: string;
    address1: string;
    address2?: string;
    city: string;
    state: number;
    zip: string;
    country: number;
    industry: string;
    certificates: string[];
    inspection: string[];
    paymentTerms: string;
    // Buyer contact information
    fullName?: string;
    companyName?: string;
    email?: string;
    phoneNo?: string;
  };
  partDetails: RfqPart[];
}

// Interface for the S3 file upload response
export interface FileUploadResponseData {
  fileName: string;
  fileUrl: string;
}

export interface FileUploadResponse {
  data: FileUploadResponseData;
  // Add other properties from the response if needed, e.g.:
  // status?: string;
  // message?: string;
  // result?: boolean;
}

// Supplier details interfaces
export interface SupplierMetadata {
  company_name: string;
  company_url?: string;
  logo_url?: string;
  employee_size?: string;
  my_capabilities?: string[];
  my_equipment?: string[];
  industry_serving?: string[];
  my_address?: string;
  zip_code?: string;
  process_rates?: unknown[];
  additional_info?: unknown[];
}

export interface SupplierData {
  companyGUID: string;
  companyId: number;
  metaData: string; // JSON string
  contactMetaData: string;
  createdOn: string;
}

export interface SupplierDetailsResponse {
  status: boolean;
  data: SupplierData | null;
  message: string;
  errors: string[];
}

@Injectable({
    providedIn: 'root'
})
export class RfqIntakeService extends BaseService {
    private baseApiUrl: string;
    private baseDynamoApiUrl: string;
     companyGuid: string = '';
    
    constructor(
        private apiService: ApiService,
        private indexedDBService: IndexedDBService
    ) {
        super();
        this.baseDynamoApiUrl = environment.DynamoDBAPI;
        this.baseApiUrl = environment.APIEndpoint;
    }

    setCompanyGuid(guid: string) {
        this.companyGuid = guid;
    }

    getRfqDetailDropdowns(): Observable<any> {
        return this.apiService.get<any>(
            this.baseDynamoApiUrl,
            `masterdata/GetAllDropDownData`
        );
    }
    
    getRfqSummaryData(rfqId?: string): Observable<RfqSummaryData> {
        // Use indexedDBService to get the data from IndexedDB
        return this.indexedDBService.getRfqIntakeData(rfqId || '').pipe(
            map((data: any) => {
                if (!data) {
                    console.warn('No data found in IndexedDB for rfqId:', rfqId);
                    // Return default data if no data found
                    return {};
                }
                return data;
            }),
            catchError(error => {
                console.error('Error getting data from IndexedDB:', error);
                // Return default data in case of error
                return of();
            })
        );
    }
    
    
    
    getRfqParts(rfqId?: string): Observable<RfqPart[]> {
        // Use indexedDBService to get the RFQ data
        return this.indexedDBService.getRfqIntakeData(rfqId || '').pipe(
            map((data: any) => {
                if (!data || !data.partDetails || !Array.isArray(data.partDetails)) {
                    console.warn('No parts data found in IndexedDB for rfqId:', rfqId);
                    // Return default parts if no data found
                    return [];
                }
                return data.partDetails;
            }),
            catchError(error => {
                console.error('Error getting parts data from IndexedDB:', error);
                // Return default parts in case of error
                return of([]);
            })
        );
    }
    submitRfq(rfqId: string): Observable<any> {
        return this.getRfqSummaryData(rfqId).pipe(
            map((rfqData: RfqSummaryData) => {
                if (!rfqData || !rfqData.rfqDetails || !rfqData.partDetails || rfqData.partDetails.length === 0) {
                    throw new Error('Invalid RFQ data for submission');
                }
                
                // Extract the first part for the current API structure
                const firstPart = rfqData.partDetails[0];
                console.log("firstPart files---->", firstPart.files);
                
                // Map local files to the expected format for the API
                const mappedFiles = firstPart.files ? firstPart.files.map(file => {
                    return {
                        fileName: file.fileName,
                        fileUrl: file.fileUrl,
                        fileType: file.fileType
                    };
                }) : [];

                console.log("Mapped files---->", mappedFiles);

                // Function to format date using moment UTC
                const formatDateToUTC = (dateStr: string): string => {
                    // Parse the local date and convert to UTC while preserving the time
                    return moment(dateStr).utc().format();
                };
                
                // Build the request payload from rfqData
                const formData = {
                    "companyGuid": this.companyGuid,
                    "buyerName": rfqData.rfqDetails.fullName || "", 
                    "buyerCompanyName": rfqData.rfqDetails.companyName || "", 
                    "buyerCompanyEmail": rfqData.rfqDetails.email || "", 
                    "buyerCompanyPhone": rfqData.rfqDetails.phoneNo || "", 
                    "source": "SUW",

                    "rfqName": rfqData.rfqDetails.rfqName,
                    "quoteBy": formatDateToUTC(rfqData.rfqDetails.quoteBy),
                    "deliverBy": formatDateToUTC(rfqData.rfqDetails.deliverBy),                    
                    "buyerCompanyAddress": rfqData.rfqDetails.address1,
                    "buyerCompanyAddress2": rfqData.rfqDetails.address2 || "",
                    "buyerCompanyState": rfqData.rfqDetails.state.toString(),
                    "buyerCompanyCity": rfqData.rfqDetails.city,
                    "buyerCompanyCountry": rfqData.rfqDetails.country.toString(),
                    "buyerCompanyZipCode": rfqData.rfqDetails.zip,
                    "certificateAndQualification": rfqData.rfqDetails.certificates || [],
                    "industry": rfqData.rfqDetails.industry,
                    "inspection": rfqData.rfqDetails.inspection || [],
                    "paymentTerms": rfqData.rfqDetails.paymentTerms,
                    "rfqPartDetails": {
                        "rfqPartId": firstPart.rfqPartId,
                        "partName": firstPart.partName,
                        "partNumber": firstPart.partNumber,
                        "notes": firstPart.notes,
                        "materialSpecifications": firstPart.material.toString(),
                        "materialGrade": firstPart.materialGrade,
                        "finish": firstPart.finishes || [],
                        "tolerance": firstPart.tolerance,
                        "qty1": firstPart.quantity,
                        "files": mappedFiles
                    }
                };
                
                return formData;
            }),
            switchMap(formData => {
                return this.apiService.post<any>(
                    this.baseDynamoApiUrl,
                    `rfq/SavePrivateRFQDetails`,
                    formData
                );
            })
        );
    }

    getCountryList(): Observable<any> { 
        return this.apiService.get<any>(
            this.baseDynamoApiUrl,
            `masterdata/GetAllCountries`
        );
    }
    getStateList(countryId: string): Observable<any> { 
        return this.apiService.get<any>(    
            this.baseDynamoApiUrl,
            `masterdata/GetStatesByCountryId/${countryId}`
        );
    }

    uploadFileToS3(file: File): Observable<FileUploadResponse> {
        // console.log(file); // Original console log

        const formData = new FormData();
        formData.append('file', file, file.name);

        // TODO: Replace with your actual token retrieval logic
        const token = 'YOUR_AUTH_TOKEN_HERE'; // e.g., AppStorageUtil.getItem('Token')
        
        let headers = new HttpHeaders();
        if (token) {
          headers = headers.set('Authorization', 'bearer ' + token);
        }
        // HttpClient will typically set the Content-Type for FormData correctly.
        // No need to set Content-Type: multipart/form-data manually here.

        return this.apiService.post<FileUploadResponse>(
            this.baseDynamoApiUrl,
            `RFQ/UploadFileToS3`,
            formData, // Sending FormData
            headers   // Sending headers with Authorization
        );
    }

    /**
     * Get supplier details by company GUID
     * @param companyGuid The company GUID to fetch details for
     * @returns Observable with supplier details response
     */
    getSupplierDetailsByCompanyGuid(companyGuid: string): Observable<SupplierDetailsResponse> {
        return this.apiService.get<SupplierDetailsResponse>(
            this.baseDynamoApiUrl,
            `Supplier/GetQASupplierDetailsByCompanyGuid`,
            { companyGuid }
        );
    }

    /**
     * Parse supplier metadata JSON string
     * @param metaDataString The JSON string to parse
     * @returns Parsed metadata object or null if parsing fails
     */
    parseSupplierMetadata(metaDataString: string): SupplierMetadata | null {
        try {
            return JSON.parse(metaDataString) as SupplierMetadata;
        } catch {
            return null;
        }
    }
    

    
}